import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { faPlayPause } from '@fortawesome/pro-light-svg-icons/faPlayPause';
import { TypedMemo } from '../../../common/types';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useSMLPaginationParam } from '../../hooks/useSMLPaginationParam';
import { SMLResultsCard } from '../SMLResultsCard/SMLResultsCard';
import { SMLResultsItem } from '../SMLResultsCard/props';
import { PositionChangeElement } from '../PositionChangeElement/PositionChangeElement';
import { useTracksTopOverall } from '../../hooks/useTracksTopOverallHook';
import { useSMLCountryParam } from '../../hooks/useSMLCountryParam';
import { useSMLGenreParam } from '../../hooks/useSMLGenreParam';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { countryParamName } from '../SMLCountrySelector/consts';
import { genreParamName } from '../SMLGenreSelector/consts';
import { TopOverallTracksCardProps } from './props';

const paginationParamName = 'overallPage';

const TopOverallTracksCardComponent: React.FC<TopOverallTracksCardProps> = ({ artistId }) => {
  const intl = useIntl();
  const { selectedDate } = useSMLDatePickerDateParam();
  const { currentPage } = useSMLPaginationParam(paginationParamName);
  const { countryCode } = useSMLCountryParam();
  const { genre } = useSMLGenreParam();

  const {
    data: tracksData,
    isLoading,
    error,
    refetch,
  } = useTracksTopOverall({
    page: currentPage,
    date: selectedDate,
    artistId,
  });

  const tracks = useMemo(() => tracksData?.tracks || [], [tracksData?.tracks]);

  const items: SMLResultsItem[] = useMemo(
    () =>
      tracks.map((track: any) => ({
        id: track.trackId,
        imageUrl: track.imageUrl,
        name: `${track.mainTrackName}${track.artistNames.length > 0 ? ` - ${track.artistNames.join(', ')}` : ''}`,
        position: track.overallPosition.current,
        rightElement: (
          <PositionChangeElement
            previousPosition={track.overallPosition.previous}
            currentPosition={track.overallPosition.current}
          />
        ),
        chart: { entityType: 'track' as const, date: selectedDate },
        route: SMLRoutes.trackDetails,
        params: {
          trackId: track.trackId,
          date: date.format(selectedDate, 'YYYY-MM-DD'),
          ...(countryCode !== undefined ? { [countryParamName]: countryCode } : {}),
          ...(genre !== undefined ? { [genreParamName]: genre } : {}),
        },
      })),
    [tracks, selectedDate, countryCode, genre]
  );

  const title = artistId
    ? intl.formatMessage({
        id: 'app.sml.artist.topOverallTracks.title',
        defaultMessage: 'Top Tracks for Artist',
      })
    : intl.formatMessage({
        id: 'app.sml.home.topOverallTracks.title',
        defaultMessage: 'Top Tracks Overall',
      });

  const subtitle = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <SMLResultsCard
      title={title}
      subtitle={subtitle}
      icon={faPlayPause}
      iconColor="var(--brand-violet-lift)"
      items={items}
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        refetch().catch(console.error);
      }}
      imageShape="rounded-square"
      showCountryFilter={artistId === undefined}
      showGenreFilter={artistId === undefined}
      paginationParamName={paginationParamName}
    />
  );
};

export const TopOverallTracksCard = TypedMemo(TopOverallTracksCardComponent);
